import { parseIsoInstant } from './timeGrid'
import { rowTone } from './timelineView'

// The mini tapes «Before» / «After» of the preview (RN61 [D11], maqueta miniTape / panelRows). Pure: from the body of a
// GET timeline (the raw block of one machine) to the rows to draw, and from `changes` plus the «before» to the marks.

const LANE_TABLE = {
  deviceStatus: 'DEVICE_STATUS',
  jobStates: 'DEVICE_FABRICATION_ORDER',
  presence: 'DEVICE_WORKER',
  imputations: 'DEVICE_WORKER_FABRICATION_ORDER'
}
const LANE_KIND = { deviceStatus: 'status', jobStates: 'job', presence: 'presence', imputations: 'imputations' }

const segmentsOf = (laneName, rows, range, nowMs) => rows.map((row) => {
  const startMs = Math.max(parseIsoInstant(row.start).getTime(), range.fromMs)
  const realEnd = row.open === true ? nowMs : parseIsoInstant(row.end).getTime()
  const endMs = Math.min(realEnd, range.toMs)
  return { key: `${laneName}:${row.rowId}`, laneName, rowId: row.rowId, startMs, endMs, tone: rowTone(LANE_KIND[laneName], row), mark: 'none' }
}).filter((segment) => segment.endMs > segment.startMs)

/**
 * The rows of a mini tape (RN61): the presence and the clock-ins of the worker; with a job anchor also the job and the
 * clock-ins of the other workers of the machine; and the machine when it is OFFLINE (its state is corrected too).
 * `block` = the raw block of the machine, `anchor` = `{ workerId, jobId }` (jobId 0 = presence anchor).
 */
export const buildMiniRows = ({ block, anchor, range, nowMs, names, t_ }) => {
  const own = (rows) => rows.filter((row) => row.worker && row.worker.id === anchor.workerId)
  const rows = [
    { key: 'presence', label: t_('{{worker}} · presence', { worker: names.worker(anchor.workerId) }), segments: segmentsOf('presence', own(block.lanes.presence), range, nowMs) },
    { key: 'imputations', label: t_('{{worker}} · clock-ins', { worker: names.worker(anchor.workerId) }), segments: segmentsOf('imputations', own(block.lanes.imputations), range, nowMs) }
  ]
  if (anchor.jobId !== 0) {
    const job = block.lanes.jobStates.find((row) => row.job && row.job.id === anchor.jobId)
    rows.push({
      key: 'job',
      label: t_('Job {{job}}', { job: job ? (job.job.name || job.job.code) : `#${anchor.jobId}` }),
      segments: segmentsOf('jobStates', block.lanes.jobStates.filter((row) => row.job && row.job.id === anchor.jobId), range, nowMs)
    })
    block.workers.filter((worker) => worker.id !== anchor.workerId).forEach((worker) => rows.push({
      key: `imputations-${worker.id}`,
      label: t_('{{worker}} · clock-ins', { worker: names.worker(worker.id) }),
      segments: segmentsOf('imputations', block.lanes.imputations.filter((row) => row.worker && row.worker.id === worker.id), range, nowMs)
    }))
  }
  if (block.device.mode === 'OFFLINE') rows.push({ key: 'status', label: t_('Machine'), segments: segmentsOf('deviceStatus', block.lanes.deviceStatus, range, nowMs) })
  return rows
}

/** The `lane:rowId` of every row of a block: what «before» had, to tell what is new in «after» (A202-5). */
export const rowIdsOf = (block) => new Set(Object.keys(LANE_TABLE).flatMap((laneName) => block.lanes[laneName].map((row) => `${laneName}:${row.rowId}`)))

/**
 * Marks of «after» (RN61, A202-5): «adjusted» for the rows that `changes` UPDATEs (the lane and the table go together), «filled»
 * for the rows that «before» did not have (by lane + rowId, never by looking for the INSERT of `changes`, whose rowId is null).
 * Without «before» (`beforeIds` empty, A202-9) there is nothing to compare with: no row is «filled».
 */
export const markRows = (rows, { changes, beforeIds, compare }) => {
  const updated = new Set(changes.filter((change) => change.operation === 'UPDATE' && change.rowId !== null).map((change) => `${change.table}:${change.rowId}`))
  return rows.map((row) => ({
    ...row,
    segments: row.segments.map((segment) => {
      if (updated.has(`${LANE_TABLE[segment.laneName]}:${segment.rowId}`)) return { ...segment, mark: 'adjusted' }
      if (compare && !beforeIds.has(segment.key)) return { ...segment, mark: 'filled' }
      return segment
    })
  }))
}
